import { useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { captureException } from '@sentry/react';
import { Dropdown, Menu, Skeleton, Typography } from 'antd';
import { TbBuilding, TbGitCompare } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { MetricChartCompany } from '@/shared/components/data-visualization/recharts';
import { normalizeMetricChartCompany } from '@/shared/components/data-visualization/recharts';
import {
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
} from '@/shared/components/dropdowns';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import {
    DEFAULT_FINANCIAL_COMPANIES,
    enrichFinancialCompanies,
    searchEnrichedFinancialCompanies,
} from '@/shared/components/financial-statements/financialCompanySearch';
import { EmptySection } from '@/shared/components/layouts';
import { TickerExchangeTag } from '@/shared/components/tags';
import { corporateLogo } from '@/utilities';

interface MetricComparisonDropdownProps {
    company?: MetricChartCompany;
    onChange: (companies: MetricChartCompany[]) => void;
    value: MetricChartCompany[];
}

const dedupeCompanies = (companies: MetricChartCompany[]) => {
    const seen = new Set<string>();

    return companies.filter(({ symbol }) => {
        if (!symbol || seen.has(symbol)) {
            return false;
        }

        seen.add(symbol);
        return true;
    });
};

const MetricComparisonDropdown: FC<MetricComparisonDropdownProps> = ({ company, onChange, value }) => {
    const { token } = useAntDesignTheme();
    const [input, setInput] = useState('');
    const [initialLoading, setInitialLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [peers, setPeers] = useState<MetricChartCompany[]>([]);
    const [moreCompanies, setMoreCompanies] = useState<MetricChartCompany[]>([]);
    const [searchResults, setSearchResults] = useState<MetricChartCompany[]>([]);
    const [loadedSymbol, setLoadedSymbol] = useState<string>();
    const previousCompanySymbol = useRef(company?.symbol);

    useEffect(() => {
        if (!open || !company?.symbol || loadedSymbol === company.symbol) {
            return;
        }

        const controller = new AbortController();

        const loadCompanies = async () => {
            try {
                setInitialLoading(true);
                const [peerResult, moreCompaniesResult] = await Promise.allSettled([
                    authorizedAxiosInstance.get<unknown[]>(`/api/vendors/fmp/peers/${company.symbol}`, {
                        signal: controller.signal,
                    }),
                    enrichFinancialCompanies(DEFAULT_FINANCIAL_COMPANIES, controller.signal),
                ]);

                if (controller.signal.aborted) {
                    return;
                }

                if (peerResult.status === 'fulfilled') {
                    setPeers(
                        dedupeCompanies(
                            (Array.isArray(peerResult.value.data) ? peerResult.value.data : [])
                                .map((peer) => normalizeMetricChartCompany(peer))
                                .filter((peer): peer is MetricChartCompany => Boolean(peer))
                                .filter((peer) => peer.symbol !== company.symbol)
                                .sort((a, b) => (b.marketCap ?? 0) - (a.marketCap ?? 0)),
                        ),
                    );
                } else {
                    captureException(peerResult.reason);
                    setPeers([]);
                }

                if (moreCompaniesResult.status === 'fulfilled') {
                    setMoreCompanies(moreCompaniesResult.value);
                } else {
                    captureException(moreCompaniesResult.reason);
                    setMoreCompanies([]);
                }

                setLoadedSymbol(company.symbol);
            } catch (error) {
                if (!controller.signal.aborted) {
                    captureException(error);
                    setPeers([]);
                    setMoreCompanies([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setInitialLoading(false);
                }
            }
        };

        void loadCompanies();

        return () => controller.abort();
    }, [company?.symbol, loadedSymbol, open]);

    useEffect(() => {
        const query = input.trim();

        if (query.length < 2) {
            setSearchLoading(false);
            setSearchResults([]);
            return;
        }

        const controller = new AbortController();
        setSearchLoading(true);
        const timer = setTimeout(async () => {
            try {
                setSearchResults(await searchEnrichedFinancialCompanies(query, controller.signal));
            } catch (error) {
                if (!controller.signal.aborted) {
                    captureException(error);
                    setSearchResults([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setSearchLoading(false);
                }
            }
        }, 250);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [input]);

    useEffect(() => {
        if (previousCompanySymbol.current === company?.symbol) {
            return;
        }

        previousCompanySymbol.current = company?.symbol;
        setInput('');
        setLoadedSymbol(undefined);
        setMoreCompanies([]);
        setPeers([]);
        setSearchResults([]);
        onChange([]);
    }, [company?.symbol, onChange]);

    const normalizedInput = input.trim().toLocaleLowerCase();
    const filteredPeers = useMemo(
        () =>
            normalizedInput
                ? peers.filter((peer) =>
                      [peer.companyName, peer.symbol, peer.exchange, peer.sector].some((field) =>
                          field?.toLocaleLowerCase().includes(normalizedInput),
                      ),
                  )
                : peers,
        [normalizedInput, peers],
    );
    const otherCompanies = useMemo(() => {
        const source = normalizedInput.length >= 2 ? searchResults : moreCompanies;
        const excludedSymbols = new Set([company?.symbol, ...filteredPeers.map(({ symbol }) => symbol)]);

        return dedupeCompanies(source).filter(({ symbol }) => !excludedSymbols.has(symbol));
    }, [company?.symbol, filteredPeers, moreCompanies, normalizedInput.length, searchResults]);
    const availableCompanies = useMemo(
        () => dedupeCompanies([...value, ...filteredPeers, ...otherCompanies]),
        [filteredPeers, otherCompanies, value],
    );

    const toggleCompany = (symbol: string) => {
        const selected = value.some((peer) => peer.symbol === symbol);
        const nextCompany = availableCompanies.find((peer) => peer.symbol === symbol);

        onChange(
            selected ? value.filter((peer) => peer.symbol !== symbol) : nextCompany ? [...value, nextCompany] : value,
        );
    };

    const renderCompany = (candidate: MetricChartCompany) => (
        <div style={{ minWidth: '18rem' }}>
            <DropdownOptionContent
                avatar={{
                    icon: <TbBuilding />,
                    props: { size: 'small' },
                    src: candidate.website ? corporateLogo(candidate.website) : undefined,
                }}
                metadata={[
                    <TickerExchangeTag
                        companyName={candidate.companyName}
                        exchange={candidate.exchange}
                        key='ticker'
                        styles={{ color: token.colorTextSecondary, marginLeft: 0 }}
                        symbol={candidate.symbol}
                        website={candidate.website}
                    />,
                    candidate.sector,
                ]}
                selected={value.some(({ symbol }) => symbol === candidate.symbol)}
                title={
                    <Typography.Text ellipsis style={{ display: 'block', maxWidth: '14rem' }}>
                        {candidate.companyName}
                    </Typography.Text>
                }
            />
        </div>
    );
    const hasItems = Boolean(filteredPeers.length || otherCompanies.length);
    const loading = initialLoading || searchLoading;

    return (
        <Dropdown
            popupRender={() => (
                <DropdownPanel
                    maxHeight='min(30rem, 70vh)'
                    minWidth='21rem'
                    search={{
                        placeholder: 'Search Companies...',
                        setValue: setInput,
                        value: input,
                    }}
                >
                    {loading ? (
                        <div style={{ padding: '0.75rem' }}>
                            <Skeleton active paragraph={{ rows: initialLoading ? 6 : 3 }} title={false} />
                        </div>
                    ) : hasItems ? (
                        <>
                            {filteredPeers.length ? (
                                <>
                                    <DropdownMenuLabel>Competitors</DropdownMenuLabel>
                                    <DropdownPanelMenu selectable={false}>
                                        {filteredPeers.map((candidate) => (
                                            <Menu.Item
                                                key={candidate.symbol}
                                                onClick={({ domEvent }) => {
                                                    domEvent.stopPropagation();
                                                    toggleCompany(candidate.symbol);
                                                }}
                                            >
                                                {renderCompany(candidate)}
                                            </Menu.Item>
                                        ))}
                                    </DropdownPanelMenu>
                                </>
                            ) : null}
                            {otherCompanies.length ? (
                                <>
                                    <DropdownMenuLabel>More Companies</DropdownMenuLabel>
                                    <DropdownPanelMenu selectable={false}>
                                        {otherCompanies.map((candidate) => (
                                            <Menu.Item
                                                key={candidate.symbol}
                                                onClick={({ domEvent }) => {
                                                    domEvent.stopPropagation();
                                                    toggleCompany(candidate.symbol);
                                                }}
                                            >
                                                {renderCompany(candidate)}
                                            </Menu.Item>
                                        ))}
                                    </DropdownPanelMenu>
                                </>
                            ) : null}
                        </>
                    ) : !hasItems ? (
                        <EmptySection
                            description={
                                input
                                    ? 'Try another company name or ticker symbol.'
                                    : 'No competitors are available for this company.'
                            }
                            height='11rem'
                            icon={<TbBuilding size='1.25rem' />}
                            label={input ? 'No Companies Found' : 'No Companies Available'}
                        />
                    ) : null}
                </DropdownPanel>
            )}
            open={open}
            trigger={['click']}
            onOpenChange={setOpen}
        >
            <DropdownButton icon={<TbGitCompare />} size='small'>
                Compare
                {value.length ? (
                    <span
                        style={{
                            background: token.colorFillSecondary,
                            borderRadius: token.borderRadiusSM,
                            color: token.colorTextSecondary,
                            fontSize: token.fontSizeSM,
                            lineHeight: 1,
                            padding: '0.1875rem 0.3125rem',
                        }}
                    >
                        {value.length}
                    </span>
                ) : null}
            </DropdownButton>
        </Dropdown>
    );
};

export default MetricComparisonDropdown;
